import React, { useState, useEffect, useCallback } from 'react';
import CustomFormInput from '../components/forms/CustomFormInput';
import { organizationApi } from '../api/organization';
import './EmailCopyShowcase.css';

/**
 * EmailCopyShowcase Component
 *
 * Demo/showcase page rendering the copy of every email the platform sends,
 * driven by the same backend surface the Settings preview uses:
 * - GET  /api/v2/org/email-templates            → full EMAIL_TYPES list (new types auto-appear)
 * - POST /api/v2/org/email-templates/:type/preview → { subject, html } with sample data
 *
 * Requires an org login (the preview endpoint resolves the school's own
 * customised templates and branding context where applicable).
 *
 * Every sender renders from the same getSystemDefault template the preview
 * uses, so what this page shows is what recipients receive.
 */
const EmailCopyShowcase = () => {
    const [templates, setTemplates] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    const [subject, setSubject] = useState('');
    const [html, setHtml] = useState('');
    const [listError, setListError] = useState('');
    const [previewError, setPreviewError] = useState('');
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    useEffect(() => {
        organizationApi.getEmailTemplates()
            .then((res) => {
                if (res.ok && res.data?.templates) {
                    setTemplates(res.data.templates);
                    if (res.data.templates.length > 0) {
                        setSelectedType(res.data.templates[0].emailType);
                    }
                } else {
                    setListError(
                        res.status === 403 || res.status === 401
                            ? 'Log in with a school, instructor or organiser account to load email previews.'
                            : res.data?.message || 'Failed to load email type list.'
                    );
                }
            })
            .catch(() => setListError('Failed to load email type list.'));
    }, []);

    const loadPreview = useCallback((emailType) => {
        setIsLoadingPreview(true);
        setPreviewError('');
        organizationApi.previewEmailTemplate(emailType, {})
            .then((res) => {
                if (res.ok && res.data) {
                    setSubject(res.data.subject || '');
                    setHtml(res.data.html || '');
                } else {
                    setSubject('');
                    setHtml('');
                    setPreviewError(res.data?.message || 'Failed to render preview.');
                }
            })
            .catch(() => setPreviewError('Failed to render preview.'))
            .finally(() => setIsLoadingPreview(false));
    }, []);

    useEffect(() => {
        if (selectedType) loadPreview(selectedType);
    }, [selectedType, loadPreview]);

    const editableTemplates = templates.filter((t) => t.ownerInitiated);
    const systemTemplates = templates.filter((t) => !t.ownerInitiated);
    const selectedMeta = templates.find((t) => t.emailType === selectedType);

    const renderPillGroup = (group, variant) => (
        <div className="email-showcase-pill-group">
            {group.map((t) => (
                <CustomFormInput
                    key={t.emailType}
                    type="radio"
                    variant={variant}
                    layout="pill"
                    label={t.label}
                    name="email-type"
                    value={t.emailType}
                    checked={selectedType === t.emailType}
                    onChange={(e) => setSelectedType(e.target.value)}
                />
            ))}
        </div>
    );

    return (
        <div className="email-copy-showcase">
            <div className="email-showcase-header">
                <h1>Email Copy Showcase</h1>
                <p>
                    Every email the platform sends, rendered with sample data through the same
                    preview pipeline as Settings. School-editable templates reflect your school's
                    saved customisations.
                </p>
            </div>

            {listError ? (
                <div className="email-showcase-error">{listError}</div>
            ) : (
                <div className="email-showcase-layout">
                    <div className="email-showcase-selector">
                        <h2>School-editable templates</h2>
                        {renderPillGroup(editableTemplates, 'purple')}
                        <h2>System emails (auto-sent)</h2>
                        {renderPillGroup(systemTemplates, 'turquoise')}
                    </div>

                    <div className="email-showcase-preview">
                        {selectedMeta && (
                            <div className="email-showcase-meta">
                                <span className="email-showcase-type-code">{selectedMeta.emailType}</span>
                                {selectedMeta.hasCustom && (
                                    <span className="email-showcase-custom-badge">Customised by your school</span>
                                )}
                                <p className="email-showcase-trigger">{selectedMeta.trigger}</p>
                            </div>
                        )}
                        {previewError ? (
                            <div className="email-showcase-error">{previewError}</div>
                        ) : (
                            <>
                                <div className="email-showcase-subject">
                                    <span className="email-showcase-subject-label">Subject</span>
                                    <span className="email-showcase-subject-text">
                                        {isLoadingPreview ? 'Loading…' : subject}
                                    </span>
                                </div>
                                <iframe
                                    className="email-showcase-iframe"
                                    title="Email preview"
                                    sandbox=""
                                    srcDoc={html}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default EmailCopyShowcase;
